import { Component, signal, computed, effect, inject, untracked, OnInit, OnDestroy } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NavigationEnd, Router } from '@angular/router';
import { filter } from 'rxjs';
import { CommonModule } from '@angular/common';
import { MatIconModule } from '@angular/material/icon';
import { OverlayModule } from '@angular/cdk/overlay';
import { AiAgentService } from '../../services/API/ai-agent.service';
import { AiLayoutService } from '../../services/ai-layout.service';
import { UnifiedAuthService } from '../../services/unified-auth.service';
import { AiIdleHelpService } from '../../services/ai-idle-help.service';
import { GuiaOneService } from '../../services/tour-mode/guia-one.service';
import { GuiaProatividadeService } from '../../services/tour-mode/guia-proatividade.service';
import { GuiaTeaserComponent } from './guia-teaser.component';
import { AiConversaComponent } from './ai-conversa/ai-conversa.component';
import { AiAbaComponent } from './ai-aba/ai-aba.component';
import { OneIconButtonComponent } from '../one-icon-button/one-icon-button.component';

/** Overlay "modo assistente" estilo Alexa/assistente de carro: tela cheia escurecida, orb de voz
 *  na cor da marca, saudação pelo nome, aba-gaveta (`ai-aba`) flutuante quando fechado e
 *  autenticado. O corpo da conversa (bolhas, chips, input, gravação, ações) vive em `ai-conversa`
 *  desde a T3 — esta é só a CASCA: balões, aba, cabeçalho, orbe e o modo palco/chat. Visual do
 *  sistema (glass/tokens do design system) desde a T6 — a antiga `ai-tema` saiu. */
@Component({
  selector: 'ai-assistant-overlay',
  standalone: true,
  imports: [
    CommonModule, MatIconModule, OverlayModule, GuiaTeaserComponent, AiConversaComponent,
    AiAbaComponent, OneIconButtonComponent,
  ],
  templateUrl: './ai-assistant-overlay.component.html',
  styleUrl: './ai-assistant-overlay.component.css',
})
export class AiAssistantOverlayComponent implements OnInit, OnDestroy {
  readonly svc = inject(AiAgentService);
  readonly layout = inject(AiLayoutService);
  /** Cartão flutuante (modo chat) — lido pelo template no lugar do antigo modo-chat do AiAgentService. */
  readonly emChat = computed(() => this.layout.modoEfetivo() === 'chat');
  private readonly auth = inject(UnifiedAuthService);
  readonly idleHelp = inject(AiIdleHelpService);
  /** D67/D68/D69/D71 — dono das ações do orbe (chips) e do atalho `?`. */
  readonly guia = inject(GuiaOneService);
  /** D72/D73/D75/D76 — dono do balão único do guia, já arbitrado; idle-help cede a ele. */
  readonly proatividade = inject(GuiaProatividadeService);

  /** D69 — desinstalador do atalho `?` devolvido por `guia.instalarAtalho()`; chamado no destroy. */
  private desinstalarAtalho: (() => void) | null = null;

  ngOnInit(): void {
    this.idleHelp.iniciar();
    this.proatividade.iniciar();
    this.desinstalarAtalho = this.guia.instalarAtalho();
    if (this.temUsuario()) {
      void this.svc.carregarAlertas();
    }
  }

  ngOnDestroy(): void {
    this.desinstalarAtalho?.();
  }

  alternarMudo(): void {
    this.svc.mutado.set(!this.svc.mutado());
  }

  alternarModoChat(): void {
    this.layout.definirModo(this.layout.modoEfetivo() === 'chat' ? 'palco' : 'chat');
  }

  /** Botão "Acoplar à direita" do cabeçalho — respeita o piso de viewport e o tour (D8/D9). */
  acoplar(): void {
    if (this.layout.podeAcoplar()) this.layout.definirModo('painel');
  }

  /** Cor de destaque (marca da empresa) com fallback no --main do sistema. */
  get accent(): string {
    return this.svc.corMarca() || 'var(--main-100, #109ccd)';
  }

  temUsuario(): boolean {
    const u = this.auth.getUserInfos() as any;
    return !!u?.token;
  }

  private readonly balaoFechadoManualmente = signal(false);

  /** Balão teaser: some ao fechar manualmente ou ao abrir o chat; nunca reaparece na sessão
   * (spec §3.4.1) — balaoFechadoManualmente é um signal em memória, não localStorage. */
  readonly mostrarBalao = computed(() =>
    !this.svc.aberto() && this.svc.naoLidos() > 0 && !this.balaoFechadoManualmente() && this.temUsuario());

  readonly alertaDoBalao = computed(() => {
    const ativos = this.svc.alertas().filter(a => !a.lidoEm);
    return ativos.length
      ? [...ativos].sort((a, b) => b.severidade - a.severidade)[0]
      : null;
  });

  fecharBalao(): void {
    this.balaoFechadoManualmente.set(true);
  }

  /** Balão "Precisa de ajuda com esta tela?" — o alerta proativo e o balão do guia (D76) têm
   * prioridade; nunca com chat aberto ou sem usuário. (A guarda antiga `!gravando()` saiu na T3
   * com a gravação: ela só existe dentro do `ai-conversa`, que só é montado com o chat aberto —
   * logo `!svc.aberto()` já a implica.) */
  readonly mostrarBalaoAjuda = computed(() =>
    !!this.idleHelp.sugestaoAjuda() && !this.mostrarBalao() && !this.svc.aberto()
    && this.temUsuario() && !this.proatividade.teaser());

  /** Balão do tour (guia-teaser) visível: mesma regra do `[oculto]` no template. */
  readonly mostrarBalaoTour = computed(() =>
    !!this.proatividade.teaser() && !this.mostrarBalao() && !this.svc.aberto());

  /** Animação de chamada da aba (decisão do usuário, 2026-09-17): só AJUDA e TOUR chamam — o
   *  alerta de negócio (`mostrarBalao`) continua discreto. */
  readonly chamandoAba = computed(() => this.mostrarBalaoAjuda() || this.mostrarBalaoTour());

  /** Contador de chegadas da minhoca (input `disparo` da aba): incrementa a cada NOVA notificação
   *  de ajuda/tour visível (inclusive uma substituindo outra) e a cada chegada à Home. */
  readonly disparoAba = signal(0);

  /** Identidade da notificação visível agora — o objeto da sugestão de ajuda ou o `TeaserGuia`. */
  private readonly notificacaoVisivel = computed<unknown>(() =>
    this.mostrarBalaoAjuda() ? this.idleHelp.sugestaoAjuda()
      : this.mostrarBalaoTour() ? this.proatividade.teaser()
      : null);

  private readonly router = inject(Router);

  constructor() {
    let anterior: unknown = null;
    effect(() => {
      const atual = this.notificacaoVisivel();
      const nova = atual !== null && atual !== anterior;
      anterior = atual;
      if (nova) untracked(() => this.disparoAba.update(n => n + 1));
    });
    // Decisão do usuário (2026-09-18): FECHAR o assistente (painel, chat ou palco) roda a chegada
    // completa de luzes na aba mesmo sem notificação — a aba "recebe" o assistente de volta.
    let abertoAnterior = untracked(() => this.svc.aberto());
    effect(() => {
      const aberto = this.svc.aberto();
      const fechou = !aberto && abertoAnterior;
      abertoAnterior = aberto;
      if (fechou) untracked(() => this.disparoAba.update(n => n + 1));
    });
    this.router.events
      .pipe(filter((e): e is NavigationEnd => e instanceof NavigationEnd), takeUntilDestroyed())
      .subscribe(e => this.aoNavegar(e.urlAfterRedirects));
  }

  /** Chegar à Home redispara a minhoca (decisão do usuário) — só a rota raiz/home, sem query. */
  aoNavegar(url: string): void {
    const caminho = url.split('?')[0].split('#')[0];
    if (caminho === '/' || caminho === '/home') this.disparoAba.update(n => n + 1);
  }

  abrirAjudaTela(): void {
    const s = this.idleHelp.consumir();
    this.abrir();
    if (s) {
      void this.svc.converseTexto(`Me explique como funciona a tela ${s.titulo} e o que posso fazer nela`);
    }
  }

  fecharBalaoAjuda(): void {
    this.idleHelp.dispensar();
  }

  /** D77 — 2º botão do idle-help: consome a sugestão (mesmo gesto de "me explica") e abre o
   * trecho do tour desta tela em vez de perguntar ao assistente. */
  mostrarTourDaTela(): void {
    this.idleHelp.consumir();
    void this.guia.executar('tour-tela');
  }

  abrir(): void {
    this.balaoFechadoManualmente.set(true);
    this.svc.abrir();
  }

  fechar(): void {
    // A gravação é do `ai-conversa`: ele mesmo devolve o microfone ao ver `svc.aberto()` cair.
    this.svc.fechar();
  }

  rotuloEstado(): string {
    switch (this.svc.estado()) {
      case 'ouvindo': return 'Ouvindo…';
      case 'pensando': return 'Pensando…';
      case 'falando': return 'Respondendo…';
      default: return 'Toque no microfone ou digite';
    }
  }
}
